import { styled } from "@mui/material";
import { useState } from "react";
import { TopBanner } from "./top-banner/TopBanner";
import { MeetingRoom } from "@kaltura/mediaspace-shared-meeting-room";
import { Welcome } from "./welcome/Welcome";
import { HeroLayout, HeroLayoutMode } from "@kaltura/mediaspace-shared-layout";
import { translate } from "@kaltura/mediaspace-shared-utils";

const StyledLiveStudio = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
}));

interface LiveStudioProps {
    /**
     * url of the action that shows the room (launch)
     */
    roomUrl: string;
    /**
     * entry/event name to display in the top banner
     */
    eventName: string;
}

/**
 * Studio view for live-studio entries
 */
export function LiveStudio({ roomUrl, eventName }: LiveStudioProps) {
    const LOCAL_STORAGE_ITEM_NAME = "kms-live-studio-dont-show-welcome";

    const [isTopBannerVisible, setIsTopBannerVisible] = useState(true);
    const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
        // Check localStorage to determine if the welcome modal should be shown
        const dontShowWelcome = localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) === "true";
        return !dontShowWelcome;
    });

    // add "mode=attendee" to the current URL (entry page url) to generate the attendee view URL
    const getAttendeeViewUrl = () => {
        const url = new URL(document.location.href);
        url.searchParams.set("mode", "attendee");
        return url.toString();
    };

    const handleWelcomeClose = () => {
        setShowWelcomeModal(false);
    };

    const handleDontShowAgainChange = (dontShowAgain: boolean) => {
        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, dontShowAgain.toString());
    };

    return (
        <StyledLiveStudio>
            {isTopBannerVisible && (
                <TopBanner
                    bannerText={translate("This is webcast studio for: %1", [eventName])}
                    buttonText={translate("Attendee view")}
                    buttonUrl={getAttendeeViewUrl()}
                    buttonTarget="_blank"
                    onClose={() => setIsTopBannerVisible(false)}
                />
            )}
            <HeroLayout mode={HeroLayoutMode.FullSize} marginBottom={isTopBannerVisible ? "50px" : "0px"}>
                <MeetingRoom roomUrl={roomUrl} title={translate("Live Studio")} />
            </HeroLayout>

            <Welcome
                open={showWelcomeModal}
                onClose={handleWelcomeClose}
                onDontShowAgainChange={handleDontShowAgainChange}
            />
        </StyledLiveStudio>
    );
}

export default LiveStudio;
